<template>
  <div class="contacts-crm">
    <!-- Hero -->
    <section class="cc-hero">
      <div class="cc-hero__glow" />
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-12">
        <div class="d-flex align-items-center">
          <div class="cc-hero__icon mr-1">
            <feather-icon
              icon="UsersIcon"
              size="24"
            />
          </div>
          <div>
            <h2 class="mb-0 cc-hero__title">
              Contacts
            </h2>
            <p class="mb-0 cc-hero__subtitle">
              Manage your customer relationships and contact details.
            </p>
          </div>
        </div>
        <div class="cc-hero__actions">
          <button
            type="button"
            class="cc-btn cc-btn--refresh"
            :disabled="dataLoad"
            @click="getAllData(currentPage)"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'cc-spin-icon': dataLoad }"
            />
            Refresh
          </button>
        </div>
      </div>

      <div class="cc-stats">
        <div class="cc-stat">
          <div class="cc-stat__icon cc-stat__icon--primary">
            <feather-icon
              icon="UserIcon"
              size="18"
            />
          </div>
          <div>
            <div class="cc-stat__value">
              {{ totalRows }}
            </div>
            <div class="cc-stat__label">
              Total contacts
            </div>
          </div>
        </div>
        <div class="cc-stat">
          <div class="cc-stat__icon cc-stat__icon--success">
            <feather-icon
              icon="Edit3Icon"
              size="18"
            />
          </div>
          <div>
            <div class="cc-stat__value">
              {{ withSignatureCount }}
            </div>
            <div class="cc-stat__label">
              With signature
            </div>
          </div>
        </div>
        <div class="cc-stat">
          <div class="cc-stat__icon cc-stat__icon--info">
            <feather-icon
              icon="AwardIcon"
              size="18"
            />
          </div>
          <div>
            <div class="cc-stat__value">
              {{ withStampCount }}
            </div>
            <div class="cc-stat__label">
              With stamp
            </div>
          </div>
        </div>
        <div class="cc-stat">
          <div class="cc-stat__icon cc-stat__icon--accent">
            <feather-icon
              icon="BriefcaseIcon"
              size="18"
            />
          </div>
          <div>
            <div class="cc-stat__value">
              {{ brokerCount }}
            </div>
            <div class="cc-stat__label">
              Brokers (this page)
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Toolbar -->
    <div class="cc-toolbar">
      <div class="cc-toolbar__row">
        <div class="cc-search">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="cc-search__icon"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="cc-search__input"
            :class="{ 'cc-search__input--has-clear': searchQuery }"
            placeholder="Search contacts by name, email or phone… (press Enter)"
            @keyup.enter="onSearch"
          >
          <button
            v-if="searchQuery"
            type="button"
            class="cc-search__clear"
            title="Clear search"
            @click="clearSearch"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <div class="cc-toolbar__controls">
          <div class="cc-broker-filter">
            <span class="cc-broker-filter__label">
              <feather-icon
                icon="FilterIcon"
                size="14"
              />
              Type
            </span>
            <div class="cc-broker-filter__pills">
              <button
                type="button"
                class="cc-broker-pill"
                :class="{ 'cc-broker-pill--active': typeFilter === null }"
                @click="setTypeFilter(null)"
              >
                All
              </button>
              <button
                type="button"
                class="cc-broker-pill cc-broker-pill--yes"
                :class="{ 'cc-broker-pill--active': typeFilter === 'broker' }"
                @click="setTypeFilter('broker')"
              >
                Brokers
              </button>
              <button
                type="button"
                class="cc-broker-pill cc-broker-pill--client"
                :class="{ 'cc-broker-pill--active': typeFilter === 'client' }"
                @click="setTypeFilter('client')"
              >
                Clients
              </button>
            </div>
          </div>

          <span class="cc-toolbar__divider" />

          <div class="cc-view-switch">
            <button
              type="button"
              class="cc-view-switch__btn"
              :class="{ 'cc-view-switch__btn--active': viewMode === 'grid' }"
              title="Card view"
              @click="setView('grid')"
            >
              <feather-icon
                icon="GridIcon"
                size="15"
              />
              Cards
            </button>
            <button
              type="button"
              class="cc-view-switch__btn"
              :class="{ 'cc-view-switch__btn--active': viewMode === 'table' }"
              title="Table view"
              @click="setView('table')"
            >
              <feather-icon
                icon="ListIcon"
                size="15"
              />
              Table
            </button>
          </div>
        </div>
      </div>

      <!-- Advanced client-side filters -->
      <div class="cc-filters">
        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="PackageIcon"
              size="14"
            />
            Products
          </span>
          <select
            v-model="filters.hasProducts"
            class="cc-filters__select"
          >
            <option value="">
              Any
            </option>
            <option value="yes">
              Has products
            </option>
            <option value="no">
              No products
            </option>
          </select>
        </label>

        <label
          v-if="access.canViewTeam"
          class="cc-filters__item"
        >
          <span class="cc-filters__label">
            <feather-icon
              icon="UserCheckIcon"
              size="14"
            />
            Salesperson
          </span>
          <select
            v-model="filters.owner"
            class="cc-filters__select"
          >
            <option value="">
              Anyone
            </option>
            <option value="unassigned">
              Unassigned
            </option>
            <option
              v-for="member in team"
              :key="member.id"
              :value="String(member.id)"
            >
              {{ memberLabel(member) }}
            </option>
          </select>
        </label>

        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="MailIcon"
              size="14"
            />
            Email
          </span>
          <select
            v-model="filters.hasEmail"
            class="cc-filters__select"
          >
            <option value="">
              Any
            </option>
            <option value="yes">
              Has email
            </option>
            <option value="no">
              No email
            </option>
          </select>
        </label>

        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="PhoneIcon"
              size="14"
            />
            Phone
          </span>
          <select
            v-model="filters.hasPhone"
            class="cc-filters__select"
          >
            <option value="">
              Any
            </option>
            <option value="yes">
              Has phone
            </option>
            <option value="no">
              No phone
            </option>
          </select>
        </label>

        <!--
          Karim, 2 Sep: "I must filter by activity and I must filter by product."
          Unlike the filters above (Products/Salesperson/Email/Phone — page-local,
          see their own note elsewhere), these two refetch the whole scoped list
          from the server: v-model would only re-narrow the 12 rows already loaded.
        -->
        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="ActivityIcon"
              size="14"
            />
            Activity
          </span>
          <select
            :value="serverFilters.activity"
            class="cc-filters__select"
            @change="setServerFilter('activity', $event.target.value)"
          >
            <option value="">
              Any
            </option>
            <option value="active">
              Active (last 90 days)
            </option>
            <option value="dormant">
              Dormant
            </option>
            <option value="none">
              No activity ever
            </option>
          </select>
        </label>

        <!--
          Karim, 3 Sep: "the activity filter should be by date so I can find the
          nearest certificate and select the number of months." / "for the last
          request, select or filter all last requests that are older than one
          month or any months." Sized by the manager, not a fixed bucket.
        -->
        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="AwardIcon"
              size="14"
            />
            Cert expires within
          </span>
          <select
            :value="serverFilters.certExpiryMonths"
            class="cc-filters__select"
            @change="setServerFilter('certExpiryMonths', $event.target.value)"
          >
            <option value="">
              Any time
            </option>
            <option value="1">
              1 month
            </option>
            <option value="3">
              3 months
            </option>
            <option value="6">
              6 months
            </option>
            <option value="12">
              12 months
            </option>
            <option value="24">
              24 months
            </option>
          </select>
        </label>

        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="ClockIcon"
              size="14"
            />
            Last request older than
          </span>
          <select
            :value="serverFilters.lastRequestOlderThanMonths"
            class="cc-filters__select"
            @change="setServerFilter('lastRequestOlderThanMonths', $event.target.value)"
          >
            <option value="">
              Any time
            </option>
            <option value="1">
              1 month
            </option>
            <option value="3">
              3 months
            </option>
            <option value="6">
              6 months
            </option>
            <option value="12">
              12 months
            </option>
            <option value="24">
              24 months
            </option>
          </select>
        </label>

        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="AwardIcon"
              size="14"
            />
            Product
          </span>
          <select
            :value="serverFilters.productId"
            class="cc-filters__select"
            @change="setServerFilter('productId', $event.target.value)"
          >
            <option value="">
              Any
            </option>
            <option
              v-for="p in productOptions"
              :key="p.id"
              :value="String(p.id)"
            >
              {{ p.label || p.crmName }}
            </option>
          </select>
        </label>

        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="StarIcon"
              size="14"
            />
            Rating
          </span>
          <select
            :value="serverFilters.tier"
            class="cc-filters__select"
            @change="setServerFilter('tier', $event.target.value)"
          >
            <option value="">
              Any
            </option>
            <option value="gold">
              Gold
            </option>
            <option value="silver">
              Silver
            </option>
            <option value="bronze">
              Bronze
            </option>
          </select>
        </label>

        <!--
          Karim, 3 Sep: "for the number of certificates the clients have, I
          can sort by numbers from lower to bigger or descending." Real
          server-side sort (like the filters above) — certificates_count needs
          the whole scoped list ordered, not just the current page re-sorted.
        -->
        <label class="cc-filters__item">
          <span class="cc-filters__label">
            <feather-icon
              icon="AwardIcon"
              size="14"
            />
            Sort by certificates
          </span>
          <select
            :value="serverFilters.certSort"
            class="cc-filters__select"
            @change="setServerFilter('certSort', $event.target.value)"
          >
            <option value="">
              Default (name)
            </option>
            <option value="desc">
              Most certificates first
            </option>
            <option value="asc">
              Fewest certificates first
            </option>
          </select>
        </label>

        <span class="cc-filters__count">
          Showing <strong>{{ visibleContacts.length }}</strong> of {{ contacts.length }} on this page
        </span>

        <button
          v-if="hasActiveFilters"
          type="button"
          class="cc-filters__clear"
          @click="clearFilters"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
          Clear filters
        </button>
      </div>

      <div
        v-if="typeFilter !== null"
        class="cc-filter-banner"
      >
        <span class="cc-filter-banner__icon">
          <feather-icon
            icon="FilterIcon"
            size="14"
          />
        </span>
        <span class="cc-filter-banner__text">
          Showing <strong>{{ typeFilterLabel }}</strong> only
        </span>
        <button
          type="button"
          class="cc-filter-banner__clear"
          @click="setTypeFilter(null)"
        >
          <feather-icon
            icon="XIcon"
            size="12"
          />
          Clear filter
        </button>
      </div>

      <!-- All loaded rows ticked, more exist beyond this page: offer the real
           "select all filtered" — Karim, 2 Sep. -->
      <div
        v-if="allLoadedSelected && !selectAllFiltered && totalRows > contacts.length"
        class="cc-filter-banner cc-filter-banner--select-all"
      >
        <span class="cc-filter-banner__text">
          All {{ contacts.length }} on this page selected.
        </span>
        <button
          type="button"
          class="cc-filter-banner__clear"
          @click="selectAllAcrossFilter"
        >
          Select all {{ totalRows }} matching the filter
        </button>
      </div>

      <!-- Bulk action bar -->
      <div
        v-if="selectionCount"
        class="cc-bulk-bar"
      >
        <span class="cc-bulk-bar__count">
          {{ selectionCount }} selected
        </span>
        <select
          v-model="bulkAssignTo"
          class="cc-filters__select"
          :disabled="bulkBusy"
        >
          <option value="">
            Assign to…
          </option>
          <option value="unassigned">
            Unassigned
          </option>
          <option
            v-for="member in team"
            :key="member.id"
            :value="String(member.id)"
          >
            {{ memberLabel(member) }}
          </option>
        </select>
        <button
          type="button"
          class="cc-btn cc-bulk-bar__btn"
          :disabled="bulkBusy || !bulkAssignTo"
          @click="bulkAssign"
        >
          Assign
        </button>
        <!-- Karim, 3 Sep: "the multi-select have only [an] assign, but I need
             also to add the rating." -->
        <select
          v-model="bulkTier"
          class="cc-filters__select"
          :disabled="bulkBusy"
        >
          <option value="">
            Rate as…
          </option>
          <option value="gold">
            Gold
          </option>
          <option value="silver">
            Silver
          </option>
          <option value="bronze">
            Bronze
          </option>
          <option value="clear">
            Clear rating
          </option>
        </select>
        <button
          type="button"
          class="cc-btn cc-bulk-bar__btn"
          :disabled="bulkBusy || !bulkTier"
          @click="bulkSetTier"
        >
          Rate
        </button>
        <button
          v-if="access.canViewTeam"
          type="button"
          class="cc-btn cc-bulk-bar__btn cc-bulk-bar__btn--danger"
          :disabled="bulkBusy"
          @click="bulkDelete"
        >
          <feather-icon
            icon="Trash2Icon"
            size="14"
          />
          Delete
        </button>
        <button
          type="button"
          class="cc-bulk-bar__clear"
          :disabled="bulkBusy"
          @click="clearSelection"
        >
          Cancel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="dataLoad"
      class="cc-loading"
    >
      <div class="cc-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading contacts…
      </p>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!visibleContacts.length"
      class="cc-empty"
    >
      <div class="cc-empty__icon">
        <feather-icon
          icon="UserXIcon"
          size="30"
        />
      </div>
      <h5 class="mb-50">
        No contacts found
      </h5>
      <p class="mb-0 text-muted">
        {{ emptyStateMessage }}
      </p>
    </div>

    <!-- Grid -->
    <div
      v-else-if="viewMode === 'grid'"
      class="cc-grid"
    >
      <!--
        THE WHOLE CARD OPENS THE CONTACT.

        Karim, 2 Sep: "when I press Contacts, it sends emails — it should open it."
        Only the <h3> name used to be clickable, while the email sat in the body as a
        full-width mailto: link. So clicking the obvious target — the card — either
        did nothing or launched the mail client. The email and phone links below stop
        propagation, so they still work when clicked deliberately.
      -->
      <article
        v-for="contact in visibleContacts"
        :key="contact.id"
        class="cc-card cc-card--clickable"
        role="button"
        tabindex="0"
        @click="goToShow(contact)"
        @keyup.enter="goToShow(contact)"
      >
        <div class="cc-card__top">
          <input
            type="checkbox"
            class="cc-card__select"
            :checked="isSelected(contact)"
            title="Select"
            @click.stop
            @change="toggleSelect(contact)"
          >
          <div
            class="cc-card__avatar"
            :style="{ background: avatarColor(contact) }"
          >
            {{ initials(contact.name) }}
          </div>
          <div class="cc-card__identity">
            <h3
              class="cc-card__name cc-card__name--link"
              :title="contact.name"
              role="button"
              tabindex="0"
              @click="goToShow(contact)"
              @keyup.enter="goToShow(contact)"
            >
              {{ contact.name || 'Unnamed contact' }}
            </h3>
            <span class="cc-card__meta">
              <span class="cc-card__id">
                <feather-icon
                  icon="HashIcon"
                  size="11"
                />
                {{ contact.erp_user_id }}
              </span>
              <span
                class="cc-badge"
                :class="isBroker(contact) ? 'cc-badge--broker' : 'cc-badge--muted'"
              >
                <feather-icon
                  :icon="isBroker(contact) ? 'BriefcaseIcon' : 'UserIcon'"
                  size="10"
                />
                {{ isBroker(contact) ? 'Broker' : 'Contact' }}
              </span>
              <span
                v-if="contact.tier"
                class="cc-badge"
                :class="`cc-badge--tier-${contact.tier}`"
              >
                <feather-icon
                  icon="AwardIcon"
                  size="10"
                />
                {{ contact.tier }}
              </span>
              <!--
                Karim, 3 Sep: "I can see the open opportunities for it from
                the table outside." Links straight into Pipeline, filtered to
                this client (?clientId=).
              -->
              <router-link
                v-if="contact.open_opportunities_count"
                :to="{ name: 'crm-system-pipeline', query: { clientId: contact.erp_user_id, clientName: contact.name } }"
                class="cc-badge cc-badge--opps"
                :title="`${contact.open_opportunities_count} open opportunit${contact.open_opportunities_count === 1 ? 'y' : 'ies'}`"
                @click.stop
              >
                <feather-icon
                  icon="TargetIcon"
                  size="10"
                />
                {{ contact.open_opportunities_count }} open
              </router-link>
            </span>
          </div>
        </div>

        <div class="cc-card__body">
          <a
            v-if="contact.email"
            :href="`mailto:${contact.email}`"
            class="cc-info"
            :title="contact.email"
            @click.stop
          >
            <span class="cc-info__icon cc-info__icon--info">
              <feather-icon
                icon="MailIcon"
                size="14"
              />
            </span>
            <span class="cc-info__text">{{ contact.email }}</span>
          </a>
          <div
            v-else
            class="cc-info cc-info--empty"
          >
            <span class="cc-info__icon cc-info__icon--info">
              <feather-icon
                icon="MailIcon"
                size="14"
              />
            </span>
            <span class="cc-info__text">No email</span>
          </div>

          <a
            v-if="contact.phone"
            :href="`tel:${contact.phone}`"
            class="cc-info"
            :title="contact.phone"
            @click.stop
          >
            <span class="cc-info__icon cc-info__icon--primary">
              <feather-icon
                icon="PhoneIcon"
                size="14"
              />
            </span>
            <span class="cc-info__text">{{ contact.phone }}</span>
          </a>
          <div
            v-else
            class="cc-info cc-info--empty"
          >
            <span class="cc-info__icon cc-info__icon--primary">
              <feather-icon
                icon="PhoneIcon"
                size="14"
              />
            </span>
            <span class="cc-info__text">No phone</span>
          </div>
        </div>

        <!-- CRM products + ownership -->
        <div class="cc-crm">
          <div class="cc-crm__row">
            <span class="cc-crm__label">
              <feather-icon
                icon="PackageIcon"
                size="12"
              />
              Products
            </span>
            <span
              v-if="isProductsLoading(contact)"
              class="cc-crm__dots"
            >…</span>
            <span
              v-else-if="!productChips(contact).length"
              class="cc-crm__none"
            >None yet</span>
            <span
              v-else
              class="cc-chips"
            >
              <span
                v-for="(chip, ci) in productChips(contact)"
                :key="ci"
                :class="{ 'cc-chip': true, 'cc-chip--unmapped': !chip.isMapped && !chip.pending, 'cc-chip--pending': chip.pending }"
                :title="chip.title"
              >{{ chip.label }}</span>
              <span
                v-if="productMoreCount(contact)"
                class="cc-chip cc-chip--more"
                :title="productMoreTitle(contact)"
              >+{{ productMoreCount(contact) }}</span>
            </span>
          </div>

          <div class="cc-crm__row">
            <span class="cc-crm__label">
              <feather-icon
                icon="BarChart2Icon"
                size="12"
              />
              Summary
            </span>
            <span class="cc-crm__value">{{ productSummary(contact) }}</span>
          </div>

          <!-- Karim, 2 Sep: a broker's facility count belongs right next to
               products/certificates "from outside" — it was only ever shown
               inside the detail page's Facilities table. -->
          <div
            v-if="contact.facility_count"
            class="cc-crm__row"
          >
            <span class="cc-crm__label">
              <feather-icon
                icon="BriefcaseIcon"
                size="12"
              />
              Facilities
            </span>
            <span class="cc-crm__value">
              {{ contact.facility_count }} facilit{{ contact.facility_count === 1 ? 'y' : 'ies' }}
            </span>
          </div>

          <div
            v-if="contact.last_request_status_label"
            class="cc-crm__row"
          >
            <span class="cc-crm__label">
              <feather-icon
                icon="FlagIcon"
                size="12"
              />
              Last Status
            </span>
            <span
              class="cc-status-pill"
              :class="`cc-status-pill--${contact.last_request_status_normalized}`"
              :title="`Jeem/Saber: ${contact.last_request_status}`"
            >{{ contact.last_request_status_label }}</span>
          </div>

          <div class="cc-crm__row">
            <span class="cc-crm__label">
              <feather-icon
                icon="UserCheckIcon"
                size="12"
              />
              Salesperson
            </span>
            <select
              v-if="access.canViewTeam"
              class="cc-owner-select"
              :disabled="isOwnerSaving(contact)"
              :value="ownerIdFor(contact)"
              @click.stop
              @change="onOwnerChange(contact, $event.target.value)"
            >
              <option value="">
                Unassigned
              </option>
              <option
                v-for="member in team"
                :key="member.id"
                :value="String(member.id)"
              >
                {{ memberLabel(member) }}
              </option>
            </select>
            <span
              v-else
              class="cc-crm__value"
            >{{ ownerNameFor(contact) || 'Unassigned' }}</span>
          </div>
        </div>

        <div class="cc-card__footer">
          <div class="cc-asset">
            <span class="cc-asset__label">
              <feather-icon
                icon="Edit3Icon"
                size="13"
              />
              Signature
            </span>
            <div class="cc-asset__box">
              <img
                v-if="contact.signature"
                :src="contact.signature"
                alt="signature"
                class="cc-asset__img"
                @click="openPreview(contact.signature, 'Signature')"
              >
              <span
                v-else
                class="cc-asset__empty"
              >
                <feather-icon
                  icon="ImageIcon"
                  size="16"
                />
                None
              </span>
            </div>
          </div>
          <div class="cc-asset">
            <span class="cc-asset__label">
              <feather-icon
                icon="AwardIcon"
                size="13"
              />
              Stamp
            </span>
            <div class="cc-asset__box">
              <img
                v-if="contact.stamp"
                :src="contact.stamp"
                alt="stamp"
                class="cc-asset__img"
                @click="openPreview(contact.stamp, 'Stamp')"
              >
              <span
                v-else
                class="cc-asset__empty"
              >
                <feather-icon
                  icon="ImageIcon"
                  size="16"
                />
                None
              </span>
            </div>
          </div>
        </div>
      </article>
    </div>

    <!-- Table -->
    <div
      v-else
      class="cc-table-wrap"
    >
      <div class="cc-table__bar">
        <div class="cc-table__bar-left">
          <span class="cc-table__bar-icon">
            <feather-icon
              icon="UsersIcon"
              size="18"
            />
          </span>
          <div>
            <h3 class="cc-table__bar-title">
              Client Directory
              <span class="cc-table__bar-count">{{ totalRows }}</span>
            </h3>
            <span class="cc-table__bar-sub">
              Showing {{ visibleContacts.length }} of {{ totalRows }} contacts
            </span>
          </div>
        </div>
      </div>

      <div class="cc-table__scroll">
        <table class="cc-table">
          <thead>
            <tr>
              <th class="cc-table__th cc-table__th--select">
                <input
                  type="checkbox"
                  :checked="allLoadedSelected"
                  title="Select all on this page"
                  @change="toggleSelectAllOnPage"
                >
              </th>
              <th class="cc-table__th cc-table__th--client">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="UserIcon"
                    size="12"
                  />
                  Contact
                </span>
              </th>
              <th class="cc-table__th">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="BriefcaseIcon"
                    size="12"
                  />
                  Company
                </span>
              </th>
              <th class="cc-table__th">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="MailIcon"
                    size="12"
                  />
                  Email
                </span>
              </th>
              <th class="cc-table__th">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="PhoneIcon"
                    size="12"
                  />
                  Phone
                </span>
              </th>
              <th class="cc-table__th cc-table__th--products">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="PackageIcon"
                    size="12"
                  />
                  Products
                </span>
              </th>
              <th class="cc-table__th cc-table__th--owner">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="UserCheckIcon"
                    size="12"
                  />
                  Default salesperson
                </span>
              </th>
              <th class="cc-table__th">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="ClockIcon"
                    size="12"
                  />
                  Activity
                </span>
              </th>
              <!--
                Karim, 3 Sep: "there are two statuses in the system: the
                status from Jeem/Saber and the status from the system
                inside... don't remove the terminated column and the status
                of GIM; it is good there." Shows BOTH: our translated status
                is the main text, the raw ERP text is the tooltip — neither
                one replaces the other.
              -->
              <th class="cc-table__th">
                <span class="cc-table__th-inner">
                  <feather-icon
                    icon="FlagIcon"
                    size="12"
                  />
                  Last Status
                </span>
              </th>
              <th class="cc-table__th cc-table__th--center">
                <span class="cc-table__th-inner cc-table__th-inner--center">
                  <feather-icon
                    icon="SettingsIcon"
                    size="12"
                  />
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="contact in visibleContacts"
              :key="contact.id"
              class="cc-table__row"
            >
              <td class="cc-table__td cc-table__td--select">
                <input
                  type="checkbox"
                  :checked="isSelected(contact)"
                  title="Select"
                  @change="toggleSelect(contact)"
                >
              </td>
              <!-- Contact -->
              <td class="cc-table__td cc-table__td--client">
                <div class="cc-table__contact">
                  <div class="cc-table__avatar-wrap">
                    <div
                      class="cc-table__avatar"
                      :style="{ background: avatarColor(contact) }"
                    >
                      {{ initials(contact.name) }}
                    </div>
                  </div>
                  <div class="cc-table__client">
                    <span
                      class="cc-table__name"
                      :title="contact.name"
                    >
                      {{ contact.name || 'Unnamed contact' }}
                    </span>
                    <span class="cc-table__id">
                      <feather-icon
                        icon="HashIcon"
                        size="10"
                      />
                      {{ contact.erp_user_id }}
                      <span
                        class="cc-badge"
                        :class="isBroker(contact) ? 'cc-badge--broker' : 'cc-badge--muted'"
                      >
                        <feather-icon
                          :icon="isBroker(contact) ? 'BriefcaseIcon' : 'UserIcon'"
                          size="10"
                        />
                        {{ isBroker(contact) ? 'Broker' : 'Contact' }}
                      </span>
                      <router-link
                        v-if="contact.open_opportunities_count"
                        :to="{ name: 'crm-system-pipeline', query: { clientId: contact.erp_user_id, clientName: contact.name } }"
                        class="cc-badge cc-badge--opps"
                        :title="`${contact.open_opportunities_count} open opportunit${contact.open_opportunities_count === 1 ? 'y' : 'ies'}`"
                        @click.stop
                      >
                        <feather-icon
                          icon="TargetIcon"
                          size="10"
                        />
                        {{ contact.open_opportunities_count }} open
                      </router-link>
                    </span>
                  </div>
                </div>
              </td>

              <!-- Company -->
              <td class="cc-table__td">
                <span
                  v-if="companyNameFor(contact)"
                  class="cc-table__ellipsis"
                  :title="companyNameFor(contact)"
                >{{ companyNameFor(contact) }}</span>
                <span
                  v-else-if="isMetaLoading(contact)"
                  class="cc-crm__dots"
                >…</span>
                <span
                  v-else
                  class="cc-table__muted"
                >—</span>
              </td>

              <!-- Email -->
              <td class="cc-table__td">
                <a
                  v-if="contact.email"
                  :href="`mailto:${contact.email}`"
                  class="cc-table__line cc-table__line--link"
                  :title="contact.email"
                  @click.stop
                >
                  <span class="cc-table__line-badge cc-table__line-badge--info">
                    <feather-icon
                      icon="MailIcon"
                      size="12"
                    />
                  </span>
                  <span class="cc-table__ellipsis">{{ contact.email }}</span>
                </a>
                <span
                  v-else
                  class="cc-table__muted"
                >No email</span>
              </td>

              <!-- Phone -->
              <td class="cc-table__td">
                <a
                  v-if="contact.phone"
                  :href="`tel:${contact.phone}`"
                  class="cc-table__line cc-table__line--link"
                  @click.stop
                >
                  <span class="cc-table__line-badge cc-table__line-badge--primary">
                    <feather-icon
                      icon="PhoneIcon"
                      size="12"
                    />
                  </span>
                  {{ contact.phone }}
                </a>
                <span
                  v-else
                  class="cc-table__muted"
                >No phone</span>
              </td>

              <!-- Products -->
              <td class="cc-table__td">
                <span
                  v-if="isProductsLoading(contact)"
                  class="cc-crm__dots"
                >…</span>
                <span
                  v-else-if="!productChips(contact).length"
                  class="cc-table__muted"
                >—</span>
                <div
                  v-else
                  class="cc-chips"
                  :title="productSummary(contact)"
                >
                  <span
                    v-for="(chip, ci) in productChips(contact)"
                    :key="ci"
                    :class="{ 'cc-chip': true, 'cc-chip--unmapped': !chip.isMapped && !chip.pending, 'cc-chip--pending': chip.pending }"
                    :title="chip.title"
                  >{{ chip.label }}</span>
                  <span
                    v-if="productMoreCount(contact)"
                    class="cc-chip cc-chip--more"
                    :title="productMoreTitle(contact)"
                  >+{{ productMoreCount(contact) }}</span>
                </div>
              </td>

              <!-- Default salesperson -->
              <td class="cc-table__td">
                <select
                  v-if="access.canViewTeam"
                  class="cc-owner-select"
                  :disabled="isOwnerSaving(contact)"
                  :value="ownerIdFor(contact)"
                  @change="onOwnerChange(contact, $event.target.value)"
                >
                  <option value="">
                    Unassigned
                  </option>
                  <option
                    v-for="member in team"
                    :key="member.id"
                    :value="String(member.id)"
                  >
                    {{ memberLabel(member) }}
                  </option>
                </select>
                <span
                  v-else
                  class="cc-table__ellipsis"
                >{{ ownerNameFor(contact) || 'Unassigned' }}</span>
              </td>

              <!-- Activity (dates) -->
              <td class="cc-table__td">
                <div class="cc-dates">
                  <div class="cc-dates__row">
                    <span class="cc-dates__icon cc-dates__icon--cert">
                      <feather-icon
                        icon="AwardIcon"
                        size="12"
                      />
                    </span>
                    <div class="cc-dates__meta">
                      <span class="cc-dates__label">Nearest Cert.</span>
                      <span
                        v-if="contact.nearest_certificate"
                        class="cc-dates__value"
                        :title="contact.nearest_certificate"
                      >{{ formatDate(contact.nearest_certificate) }}</span>
                      <span
                        v-else
                        class="cc-dates__value cc-dates__value--empty"
                      >—</span>
                    </div>
                  </div>
                  <div class="cc-dates__row">
                    <span class="cc-dates__icon cc-dates__icon--request">
                      <feather-icon
                        icon="CalendarIcon"
                        size="12"
                      />
                    </span>
                    <div class="cc-dates__meta">
                      <span class="cc-dates__label">Last Request</span>
                      <span
                        v-if="contact.last_request_date"
                        class="cc-dates__value"
                        :title="contact.last_request_date"
                      >{{ formatDate(contact.last_request_date) }}</span>
                      <span
                        v-else
                        class="cc-dates__value cc-dates__value--empty"
                      >—</span>
                    </div>
                  </div>
                  <!--
                    Karim, 3 Sep: "you are adding the product in a weird
                    way. You must add it to a column." Removed the duplicate
                    product row that lived here, inside the Activity/dates
                    cell — the dedicated Products column below (now
                    pending-aware, see productChips()/productSummary()) is
                    the one place product info lives; this cell is dates only.
                  -->
                </div>
              </td>

              <!-- Last Status -->
              <td class="cc-table__td">
                <span
                  v-if="contact.last_request_status_label"
                  class="cc-status-pill"
                  :class="`cc-status-pill--${contact.last_request_status_normalized}`"
                  :title="`Jeem/Saber: ${contact.last_request_status}`"
                >{{ contact.last_request_status_label }}</span>
                <span
                  v-else
                  class="cc-table__muted"
                >—</span>
              </td>

              <!-- Actions -->
              <td class="cc-table__td cc-table__td--center">
                <div class="cc-table__actions">
                  <button
                    type="button"
                    class="cc-act cc-act--show"
                    title="View contact"
                    @click="goToShow(contact)"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="15"
                    />
                  </button>
                  <button
                    type="button"
                    class="cc-act cc-act--edit"
                    title="Edit contact"
                    @click="goToEdit(contact)"
                  >
                    <feather-icon
                      icon="Edit2Icon"
                      size="15"
                    />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="!dataLoad"
      class="cc-pagination"
    >
      <div class="cc-pagination__info">
        Showing
        <span class="font-weight-bold">{{ rangeStart }}</span>
        -
        <span class="font-weight-bold">{{ rangeEnd }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <!-- Karim, 3 Sep: "I want to list all items, everything, not only per
           page." 5000 comfortably covers the whole scoped list. -->
      <label class="cc-pagination__size">
        <span>Per page</span>
        <select
          v-model.number="perPage"
          class="cc-filters__select"
          @change="onPageSizeChange"
        >
          <option
            v-for="n in pageSizeOptions"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
          <option :value="5000">
            All
          </option>
        </select>
      </label>
      <b-pagination
        v-if="totalRows > perPage"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        first-number
        last-number
        @change="onPageChange"
      />
    </div>

    <!-- Image preview lightbox -->
    <div
      v-if="preview.src"
      class="cc-lightbox"
      @click.self="closePreview"
    >
      <div class="cc-lightbox__dialog">
        <div class="cc-lightbox__head">
          <span class="cc-lightbox__title">{{ preview.title }}</span>
          <button
            type="button"
            class="cc-lightbox__close"
            @click="closePreview"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </div>
        <div class="cc-lightbox__body">
          <img
            :src="preview.src"
            :alt="preview.title"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { BPagination } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

const AVATAR_COLORS = [
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #00cfe8, #5be0ef)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #ff9f43, #ffb976)',
  'linear-gradient(135deg, #ea5455, #f08182)',
  'linear-gradient(135deg, #5a8dee, #7aa5f2)',
]

export default {
  name: 'ContactsCRM',
  components: { BPagination },
  data() {
    return {
      // Was 'delegator/signed' — the ERP's raw delegator list, unscoped, so every
      // salesperson saw all 4,496 contacts. 'crm/clients' is the mirrored, scoped
      // equivalent and accepts the same ?skip=&search= this page sends.
      apiUrl: 'crm/clients',
      dataLoad: false,
      contacts: [],
      perPage: 12,
      currentPage: 1,
      totalRows: 0,
      searchQuery: '',
      typeFilter: null,
      viewMode: 'grid',
      preview: {
        src: '',
        title: '',
      },
      // CRM enrichment -----------------------------------------------------
      team: [],
      access: {
        scope: 'own', canViewTeam: false, seesAll: false, name: null,
      },
      /** clientId -> { loading, distinctProducts, totalCertificates, products: [] } */
      productsByClient: {},
      /** clientId -> { loading, ownerId, ownerName, company } */
      metaByClient: {},
      /** clientId -> true while an owner change is in flight */
      savingOwner: {},
      hydrationToken: 0,
      filters: {
        hasProducts: '',
        owner: '',
        hasEmail: '',
        hasPhone: '',
      },
      // Karim, 2 Sep: "I must filter by activity and I must filter by product."
      // Real server-side filters (unlike `filters` above) — they refetch the whole
      // scoped list, not just re-sort the 12 rows already on the page.
      serverFilters: {
        productId: '',
        activity: '',
        tier: '',
        // Karim, 3 Sep: "the activity filter should be by date so I can find
        // the nearest certificate and select the number of months" / "filter
        // all last requests that are older than one month or any months."
        certExpiryMonths: '',
        lastRequestOlderThanMonths: '',
        // Karim, 3 Sep: "sort by numbers from lower to bigger or descending."
        certSort: '',
      },
      productOptions: [],
      // Karim, 2 Sep: "multi select and select all filtered... assign, delete."
      // 3 Sep: "the multi-select have only [an] assign, but I need also to add
      // the rating."
      selectedIds: [],
      selectAllFiltered: false,
      bulkAssignTo: '',
      bulkTier: '',
      bulkBusy: false,
      // Karim, 3 Sep: "I want to list all items, everything, not only per page."
      pageSizeOptions: [12, 25, 50, 100, 500],
    }
  },
  computed: {
    hasActiveFilters() {
      const f = this.filters
      const sf = this.serverFilters
      return !!(f.hasProducts || f.owner || f.hasEmail || f.hasPhone || sf.productId || sf.activity
        || sf.tier || sf.certExpiryMonths || sf.lastRequestOlderThanMonths || sf.certSort)
    },
    /** Every contact currently loaded on this page is ticked. */
    allLoadedSelected() {
      return this.contacts.length > 0 && this.contacts.every(c => this.selectedIds.includes(c.erp_user_id))
    },
    /** How many the next bulk action would touch. */
    selectionCount() {
      return this.selectAllFiltered ? this.totalRows : this.selectedIds.length
    },
    /** Client-side filtering on top of the page returned by the API. */
    visibleContacts() {
      const f = this.filters
      return this.contacts.filter(contact => {
        if (f.hasEmail === 'yes' && !contact.email) return false
        if (f.hasEmail === 'no' && contact.email) return false
        if (f.hasPhone === 'yes' && !contact.phone) return false
        if (f.hasPhone === 'no' && contact.phone) return false

        if (f.hasProducts) {
          const entry = this.productsByClient[contact.erp_user_id]
          // Unknown yet → keep the row so the list never blanks while loading.
          if (entry && !entry.loading) {
            const has = (entry.distinctProducts || 0) > 0
            if (f.hasProducts === 'yes' && !has) return false
            if (f.hasProducts === 'no' && has) return false
          }
        }

        if (f.owner) {
          const ownerId = this.ownerIdFor(contact)
          if (f.owner === 'unassigned') {
            if (ownerId) return false
          } else if (ownerId !== f.owner) {
            return false
          }
        }

        return true
      })
    },
    withSignatureCount() {
      return this.contacts.filter(c => c.signature).length
    },
    withStampCount() {
      return this.contacts.filter(c => c.stamp).length
    },
    brokerCount() {
      return this.contacts.filter(c => this.isBroker(c)).length
    },
    typeFilterLabel() {
      if (this.typeFilter === 'broker') return 'brokers'
      if (this.typeFilter === 'client') return 'clients'
      return 'all contacts'
    },
    emptyStateMessage() {
      if (this.hasActiveFilters && this.contacts.length) {
        return 'No contacts match the active filters on this page. Try clearing them.'
      }
      if (this.searchQuery && this.typeFilter !== null) {
        return 'No contacts match your search and type filter. Try adjusting them.'
      }
      if (this.searchQuery) return 'Try adjusting your search.'
      if (this.typeFilter !== null) return `No ${this.typeFilterLabel} found. Try clearing the filter.`
      return 'Contacts will appear here once available.'
    },
    rangeStart() {
      if (!this.totalRows) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
  },
  mounted() {
    if (this.$route.query.search) {
      this.searchQuery = this.$route.query.search
    }
    if (this.$route.query.view === 'table' || this.$route.query.view === 'grid') {
      this.viewMode = this.$route.query.view
    }
    const broker = this.$route.query.is_broker
    const client = this.$route.query.is_client
    if (broker === 'true' || broker === true || broker === '1' || broker === 1) {
      this.typeFilter = 'broker'
    } else if (client === 'true' || client === true || client === '1' || client === 1) {
      this.typeFilter = 'client'
    }
    const initialPage = parseInt(this.$route.query.page, 10)
    this.fetchAccess()
    this.fetchProducts()
    this.getAllData(Number.isNaN(initialPage) || initialPage < 1 ? 1 : initialPage)
  },
  methods: {
    /* ------------------------------------------------------------------ *
     * CRM access / team
     * ------------------------------------------------------------------ */
    async fetchAccess() {
      try {
        const res = await axios.get('crm/me')
        const body = decryptData(res.data.payload)
        if (body && body.data) {
          this.access = { ...this.access, ...body.data }
        }
        if (this.access.canViewTeam) this.fetchTeam()
      } catch (e) { /* stay on the safe default: own only */ }
    },
    async fetchTeam() {
      try {
        const res = await axios.get('crm/team')
        const body = decryptData(res.data.payload)
        this.team = (body && body.data) || []
      } catch (e) { /* optional */ }
    },

    /* ------------------------------------------------------------------ *
     * Lazy per-client enrichment (products + owner/company), cached
     * ------------------------------------------------------------------ */
    /** Fetch a client's CRM products once and keep it in the cache. */
    async loadProducts(clientId) {
      if (!clientId || this.productsByClient[clientId]) return
      this.$set(this.productsByClient, clientId, {
        loading: true, distinctProducts: 0, totalCertificates: 0, products: [],
      })
      try {
        const res = await axios.get(`crm/clients/${clientId}/products`)
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || {}
        this.$set(this.productsByClient, clientId, {
          loading: false,
          distinctProducts: data.distinctProducts || 0,
          totalCertificates: data.totalCertificates || 0,
          products: Array.isArray(data.products) ? data.products : [],
        })
      } catch (e) {
        this.$set(this.productsByClient, clientId, {
          loading: false, distinctProducts: 0, totalCertificates: 0, products: [],
        })
      }
    },
    /** Fetch the CRM mirror of a client (company + default salesperson), once. */
    async loadClientMeta(clientId) {
      if (!clientId || this.metaByClient[clientId]) return
      this.$set(this.metaByClient, clientId, {
        loading: true, ownerId: '', ownerName: '', company: '',
      })
      try {
        const res = await axios.get(`crm/clients/${clientId}`)
        const body = decryptData(res.data.payload)
        const client = (body && body.data) || {}
        this.$set(this.metaByClient, clientId, {
          loading: false,
          ownerId: client.owner_id ? String(client.owner_id) : '',
          ownerName: client.owner_name || '',
          company: this.readCompanyName(client.company),
        })
      } catch (e) {
        // Not every ERP delegator is mirrored in the CRM yet — stay quiet.
        this.$set(this.metaByClient, clientId, {
          loading: false, ownerId: '', ownerName: '', company: '',
        })
      }
    },
    readCompanyName(company) {
      if (!company) return ''
      const raw = company.name
      if (!raw) return ''
      if (typeof raw === 'string') return raw
      return raw.en || raw.ar || ''
    },
    /** Run async jobs with a small concurrency cap so we never flood the API. */
    async runCapped(items, worker, limit = 5) {
      const queue = [...items]
      const runners = Array.from(
        { length: Math.min(limit, queue.length) },
        async () => {
          while (queue.length) {
            const item = queue.shift()
            // eslint-disable-next-line no-await-in-loop
            await worker(item)
          }
        },
      )
      await Promise.all(runners)
    },
    /** Enrich only the rows of the page we just rendered. */
    async hydrateVisibleRows() {
      this.hydrationToken += 1
      const token = this.hydrationToken
      const ids = this.contacts.map(c => c.erp_user_id).filter(Boolean)
      await this.runCapped(ids, async id => {
        if (token !== this.hydrationToken) return
        await this.loadProducts(id)
        if (token !== this.hydrationToken) return
        await this.loadClientMeta(id)
      }, 5)
    },

    /* ------------------------------------------------------------------ *
     * Read helpers used by the template
     * ------------------------------------------------------------------ */
    productsEntry(contact) {
      return this.productsByClient[contact && contact.erp_user_id] || null
    },
    isProductsLoading(contact) {
      const entry = this.productsEntry(contact)
      return !entry || entry.loading
    },
    isMetaLoading(contact) {
      const meta = this.metaByClient[contact && contact.erp_user_id]
      return !meta || meta.loading
    },
    /**
     * Karim, 2 Sep (screenshot): the literal word "Unmapped" was rendering as if
     * it were a product name — every certificate has a real ERP name
     * (certificateName), it just may not have a curated crm_products catalog
     * entry yet. Show the real name instead of a placeholder; `isMapped` drives
     * a quieter chip style so a curated product still stands out from a raw one.
     */
    productLabel(p) {
      return p.crmName || p.certificateName || 'Certificate'
    },
    productChips(contact) {
      const entry = this.productsEntry(contact)
      if (entry && entry.products.length) {
        return entry.products.slice(0, 3).map(p => ({
          label: this.productLabel(p),
          title: `${this.productLabel(p)}${p.regulation ? ` · ${p.regulation}` : ''} · ${p.count} certificate${p.count === 1 ? '' : 's'}`,
          isMapped: !!p.isMapped,
          pending: false,
        }))
      }
      // Karim, 3 Sep: "the products are not linked inside the contact list or
      // in card... some products are pending." No REAL certificate yet — but
      // if the last request is for a product, show it as pending rather than
      // leaving the row looking identical to someone who never applied at all.
      if (!this.isProductsLoading(contact) && contact && contact.last_request_certificate) {
        return [{
          label: contact.last_request_certificate,
          title: `${contact.last_request_certificate} — ${contact.last_request_status || 'pending'} (from the last request, source: ${contact.last_request_source || 'unknown'})`,
          isMapped: false,
          pending: true,
        }]
      }
      return []
    },
    productMoreCount(contact) {
      const entry = this.productsEntry(contact)
      if (!entry) return 0
      return Math.max(0, entry.products.length - 3)
    },
    productMoreTitle(contact) {
      const entry = this.productsEntry(contact)
      if (!entry) return ''
      return entry.products.slice(3).map(p => this.productLabel(p)).join(', ')
    },
    productSummary(contact) {
      const entry = this.productsEntry(contact)
      if (!entry || entry.loading) return '…'
      const products = entry.distinctProducts || 0
      const certs = entry.totalCertificates || 0
      if (products || certs) {
        return `${products} product${products === 1 ? '' : 's'} · ${certs} certificate${certs === 1 ? '' : 's'}`
      }
      // Karim, 3 Sep: connect the pending request to the same summary line
      // instead of just saying "no certificates" for someone mid-application.
      if (contact && contact.last_request_certificate) {
        return `Pending: ${contact.last_request_certificate} (${contact.last_request_open ? 'in progress' : contact.last_request_status || 'closed'})`
      }
      return 'No certificates yet'
    },
    companyNameFor(contact) {
      const meta = this.metaByClient[contact && contact.erp_user_id]
      if (meta && meta.company) return meta.company
      return this.readCompanyName(contact && contact.company)
    },
    ownerIdFor(contact) {
      const meta = this.metaByClient[contact && contact.erp_user_id]
      if (meta && meta.ownerId) return meta.ownerId
      if (contact && contact.owner_id) return String(contact.owner_id)
      return ''
    },
    ownerNameFor(contact) {
      const meta = this.metaByClient[contact && contact.erp_user_id]
      if (meta && meta.ownerName) return meta.ownerName
      return (contact && contact.owner_name) || ''
    },
    isOwnerSaving(contact) {
      return !!this.savingOwner[contact && contact.erp_user_id]
    },

    /* ------------------------------------------------------------------ *
     * Owner assignment
     * ------------------------------------------------------------------ */
    async onOwnerChange(contact, rawValue) {
      const clientId = contact && contact.erp_user_id
      if (!clientId) return
      const previous = this.ownerIdFor(contact)
      const ownerId = rawValue ? Number(rawValue) : null
      const member = this.team.find(m => String(m.id) === String(rawValue))

      this.$set(this.savingOwner, clientId, true)
      // Optimistic — rolled back if the API says no.
      this.setLocalOwner(clientId, rawValue || '', member ? member.name : '')
      try {
        await axios.post(`crm/clients/${clientId}/owner`, { ownerId })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Default salesperson updated',
            text: member ? `${contact.name || 'Client'} → ${member.name}` : `${contact.name || 'Client'} is now unassigned`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
      } catch (error) {
        this.setLocalOwner(clientId, previous, this.ownerNameFromId(previous))
        const forbidden = error && error.response && error.response.status === 403
        this.$toast({
          component: ToastificationContent,
          props: {
            title: forbidden ? 'Not allowed' : 'Could not update owner',
            text: forbidden
              ? 'Only managers can change the default salesperson.'
              : 'The account owner was not changed.',
            variant: 'danger',
            icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.$set(this.savingOwner, clientId, false)
      }
    },
    ownerNameFromId(ownerId) {
      const member = this.team.find(m => String(m.id) === String(ownerId))
      return member ? member.name : ''
    },
    /**
     * Karim, 2 Sep: "when selecting a person, the list of marketers' roles must
     * come back in the list and the office they are allocated to." Several people
     * can share a first name — the office is what tells them apart.
     */
    memberLabel(member) {
      if (!member) return ''
      // Karim, 3 Sep: "should be markter name - office" — crm/team now
      // defaults to markter-only, so the role parenthetical was just noise
      // repeating "(markter)" on every row. Still shown for the rare ?all=1
      // case (a genuinely mixed-role list) where it's actually informative.
      const office = member.officeName || 'No office'
      const roles = Array.isArray(member.roles) ? member.roles : []
      const mixedRoles = roles.length && !(roles.length === 1 && roles[0] === 'markter')
      return mixedRoles ? `${member.name} (${roles.join('/')}) — ${office}` : `${member.name} — ${office}`
    },
    setLocalOwner(clientId, ownerId, ownerName) {
      const meta = this.metaByClient[clientId] || {
        loading: false, ownerId: '', ownerName: '', company: '',
      }
      this.$set(this.metaByClient, clientId, { ...meta, ownerId: ownerId || '', ownerName: ownerName || '' })
    },

    clearFilters() {
      this.filters = {
        hasProducts: '', owner: '', hasEmail: '', hasPhone: '',
      }
      if (this.serverFilters.productId || this.serverFilters.activity || this.serverFilters.certSort
        || this.serverFilters.certExpiryMonths || this.serverFilters.lastRequestOlderThanMonths) {
        this.serverFilters = {
          productId: '', activity: '', tier: '', certExpiryMonths: '', lastRequestOlderThanMonths: '', certSort: '',
        }
        this.getAllData(1)
      }
    },
    setServerFilter(key, value) {
      if (this.serverFilters[key] === value) return
      this.serverFilters[key] = value
      this.getAllData(1)
    },
    /* -------------------- Multi-select + bulk actions -------------------- */
    toggleSelect(contact) {
      this.selectAllFiltered = false
      const id = contact.erp_user_id
      const idx = this.selectedIds.indexOf(id)
      if (idx > -1) this.selectedIds.splice(idx, 1)
      else this.selectedIds.push(id)
    },
    isSelected(contact) {
      return this.selectedIds.includes(contact.erp_user_id)
    },
    toggleSelectAllOnPage() {
      this.selectAllFiltered = false
      if (this.allLoadedSelected) {
        this.selectedIds = []
      } else {
        this.selectedIds = this.contacts.map(c => c.erp_user_id)
      }
    },
    selectAllAcrossFilter() {
      this.selectAllFiltered = true
    },
    clearSelection() {
      this.selectedIds = []
      this.selectAllFiltered = false
      this.bulkAssignTo = ''
      this.bulkTier = ''
    },
    /** scope=ids / scope=filter, whichever the manager actually picked. */
    bulkScopeParams() {
      return this.selectAllFiltered
        ? { scope: 'filter', ...this.buildFilterParams() }
        : { scope: 'ids', ids: this.selectedIds }
    },
    async bulkAssign() {
      if (!this.selectionCount || this.bulkBusy) return
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/clients/bulk-action', {
          action: 'assign',
          ownerId: this.bulkAssignTo && this.bulkAssignTo !== 'unassigned' ? this.bulkAssignTo : null,
          ...this.bulkScopeParams(),
        })
        const body = decryptData(res.data.payload)
        const changed = body?.data?.changed ?? 0
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Assigned',
            text: `${changed} contact${changed === 1 ? '' : 's'} updated`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.clearSelection()
        await this.getAllData(this.currentPage)
      } catch (e) {
        this.showError(e, 'Bulk assign failed')
      } finally {
        this.bulkBusy = false
      }
    },
    /** Karim, 3 Sep: "the multi-select have only [an] assign, but I need also
     * to add the rating." Same scope=ids/filter shape as bulkAssign/bulkDelete. */
    async bulkSetTier() {
      if (!this.selectionCount || this.bulkBusy || !this.bulkTier) return
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/clients/bulk-action', {
          action: 'tier',
          // `newTier`, NOT `tier` — `tier` is the RATING FILTER (also present
          // when scope=filter spreads buildFilterParams() below); reusing the
          // same key would let a stray active tier filter overwrite the value
          // being set, or filter the selection down to only-already-that-tier
          // rows before the action runs.
          newTier: this.bulkTier === 'clear' ? null : this.bulkTier,
          ...this.bulkScopeParams(),
        })
        const body = decryptData(res.data.payload)
        const changed = body?.data?.changed ?? 0
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Rated',
            text: `${changed} contact${changed === 1 ? '' : 's'} updated`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.clearSelection()
        await this.getAllData(this.currentPage)
      } catch (e) {
        this.showError(e, 'Bulk rating failed')
      } finally {
        this.bulkBusy = false
      }
    },
    async bulkDelete() {
      if (!this.selectionCount || this.bulkBusy) return
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Remove ${this.selectionCount} contact${this.selectionCount === 1 ? '' : 's'} from the CRM? This hides them here — the underlying ERP record is untouched.`)) {
        return
      }
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/clients/bulk-action', {
          action: 'delete',
          ...this.bulkScopeParams(),
        })
        const body = decryptData(res.data.payload)
        const changed = body?.data?.changed ?? 0
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Removed',
            text: `${changed} contact${changed === 1 ? '' : 's'} removed`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.clearSelection()
        await this.getAllData(1)
      } catch (e) {
        this.showError(e, 'Bulk delete failed')
      } finally {
        this.bulkBusy = false
      }
    },
    async fetchProducts() {
      try {
        const res = await axios.get('crm/products')
        const body = decryptData(res.data.payload)
        this.productOptions = Array.isArray(body?.data?.products) ? body.data.products : []
      } catch (e) {
        this.productOptions = []
      }
    },

    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return typeof value === 'string' ? value.split('T')[0] : value
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    initials(name) {
      if (!name) return '??'
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    avatarColor(contact) {
      const key = contact.erp_user_id || (contact.name ? contact.name.length : 0)
      return AVATAR_COLORS[key % AVATAR_COLORS.length]
    },
    isBroker(contact) {
      return [true, 1, '1', 'true'].includes(contact?.is_broker)
    },
    setTypeFilter(value) {
      if (this.typeFilter === value) return
      this.typeFilter = value
      this.getAllData(1)
    },
    setView(mode) {
      if (this.viewMode === mode) return
      this.viewMode = mode
      this.syncQuery()
    },
    goToShow(contact) {
      this.$router.push({ name: 'crm-system-contact-show', params: { id: contact.erp_user_id } })
    },
    goToEdit(contact) {
      this.$router.push({ name: 'crm-system-contact-edit', params: { id: contact.erp_user_id } })
    },
    openPreview(src, title) {
      this.preview = { src, title }
    },
    closePreview() {
      this.preview = { src: '', title: '' }
    },
    onSearch() {
      this.getAllData(1)
    },
    clearSearch() {
      this.searchQuery = ''
      this.getAllData(1)
    },
    onPageChange(page) {
      this.getAllData(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    syncQuery() {
      const search = this.searchQuery.trim()
      const query = {}
      if (this.currentPage > 1) query.page = String(this.currentPage)
      if (search) query.search = search
      if (this.typeFilter === 'broker') query.is_broker = 'true'
      if (this.typeFilter === 'client') query.is_client = 'true'
      if (this.viewMode === 'table') query.view = 'table'

      const current = this.$route.query
      const changed = current.page !== query.page
        || current.search !== query.search
        || current.is_broker !== query.is_broker
        || current.is_client !== query.is_client
        || current.view !== query.view
      if (changed) {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    /**
     * The filter set that defines "everyone currently matching the screen" — used
     * both for the paginated fetch and for bulk-action's scope=filter (Karim, 2
     * Sep: "select all filtered"), so a bulk assign/delete can never touch a
     * different set of rows than what the manager is actually looking at.
     */
    buildFilterParams() {
      const params = {}
      const search = this.searchQuery.trim()
      if (search) params.search = search
      if (this.typeFilter === 'broker') {
        params.is_broker = true
      } else if (this.typeFilter === 'client') {
        params.is_client = true
      }
      if (this.serverFilters.productId) params.product_id = this.serverFilters.productId
      if (this.serverFilters.activity) params.activity = this.serverFilters.activity
      if (this.serverFilters.tier) params.tier = this.serverFilters.tier
      if (this.serverFilters.certExpiryMonths) params.certExpiryMonths = this.serverFilters.certExpiryMonths
      if (this.serverFilters.lastRequestOlderThanMonths) {
        params.lastRequestOlderThanMonths = this.serverFilters.lastRequestOlderThanMonths
      }
      if (this.serverFilters.certSort) {
        params.sortBy = 'certificates'
        params.sortDir = this.serverFilters.certSort
      }
      return params
    },
    /** Karim, 3 Sep: "I want to list all items, everything, not only per page." */
    onPageSizeChange() {
      this.getAllData(1)
    },
    async getAllData(page = 1) {
      this.currentPage = page
      this.dataLoad = true
      this.syncQuery()
      this.clearSelection()
      try {
        const params = {
          skip: (page - 1) * this.perPage,
          limit: this.perPage,
          ...this.buildFilterParams(),
        }

        const response = await axios.get(this.apiUrl, { params })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data || {}
          const listKey = Object.keys(data).find(key => Array.isArray(data[key]))
          const list = listKey ? data[listKey] : []
          this.contacts = Array.isArray(list) ? list : []
          this.totalRows = data.count ?? this.contacts.length
        }
      } catch (e) {
        this.showError(e, 'Failed to load contacts')
      } finally {
        this.dataLoad = false
      }
      // Enrich only the rows we just rendered, 5 at a time, cached forever.
      this.hydrateVisibleRows()
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" src="./contacts-crm.scss"></style>
<style lang="scss" scoped>
/* ---- advanced filters row ---- */
.cc-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: .6rem;
  margin-top: .75rem;
  padding-top: .75rem;
  border-top: 1px solid #f0f0f5;
}
.cc-filters__item {
  display: flex;
  align-items: center;
  gap: .35rem;
  margin-bottom: 0;
}
.cc-filters__label {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  font-size: .72rem;
  font-weight: 600;
  color: #6e6b7b;
  text-transform: uppercase;
  letter-spacing: .3px;
}
.cc-filters__select {
  border: 1px solid #e4e4ec;
  border-radius: .35rem;
  background: #fff;
  color: #5e5873;
  font-size: .78rem;
  padding: .3rem 1.4rem .3rem .5rem;
  cursor: pointer;
  outline: none;

  &:focus { border-color: #7367f0; }
}
.cc-filters__count {
  margin-left: auto;
  font-size: .76rem;
  color: #6e6b7b;
}
.cc-filters__clear {
  display: inline-flex;
  align-items: center;
  gap: .3rem;
  border: 1px solid rgba(234, 84, 85, .35);
  background: rgba(234, 84, 85, .08);
  color: #ea5455;
  border-radius: .35rem;
  font-size: .76rem;
  font-weight: 600;
  padding: .3rem .6rem;
  cursor: pointer;

  &:hover { background: rgba(234, 84, 85, .16); }
}

/* ---- product chips ---- */
.cc-chips {
  display: inline-flex;
  flex-wrap: wrap;
  gap: .25rem;
}
.cc-chip {
  display: inline-flex;
  align-items: center;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: .12rem .4rem;
  border-radius: .8rem;
  font-size: .68rem;
  font-weight: 600;
  background: rgba(115, 103, 240, .12);
  color: #7367f0;
}
.cc-chip--more {
  background: rgba(0, 207, 232, .14);
  color: #00cfe8;
}
/* A certificate with no curated crm_products catalog entry — shows the real
   ERP certificate name (never the literal word "Unmapped"), styled quieter so
   a curated product still stands out. See CrmProductController::clientProducts. */
.cc-chip--unmapped {
  background: rgba(107, 107, 128, .12);
  color: #6b6b80;
  font-weight: 500;
  font-style: italic;
}

/* A product with no certificate yet — the last request's product, not a real
   catalog match. Karim, 3 Sep: "some products are pending." */
.cc-chip--pending {
  background: rgba(255, 159, 67, .14);
  color: #b9690e;
  font-weight: 600;
}

/* ---- card CRM block ---- */
.cc-crm {
  display: flex;
  flex-direction: column;
  gap: .35rem;
  padding: .55rem .25rem 0;
  border-top: 1px dashed #eceaf5;
  margin-top: .5rem;
}
.cc-crm__row {
  display: flex;
  align-items: center;
  gap: .4rem;
  min-height: 22px;
}
.cc-crm__label {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  min-width: 88px;
  font-size: .68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .3px;
  color: #6e6b7b;
}
.cc-crm__value {
  font-size: .74rem;
  color: #5e5873;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cc-crm__none {
  font-size: .74rem;
  color: #b9b9c3;
}
.cc-crm__dots {
  font-size: .9rem;
  color: #b9b9c3;
  letter-spacing: 1px;
}

/* ---- owner select ---- */
.cc-owner-select {
  max-width: 170px;
  border: 1px solid #e4e4ec;
  border-radius: .35rem;
  background: #fff;
  color: #5e5873;
  font-size: .74rem;
  padding: .2rem 1.2rem .2rem .4rem;
  cursor: pointer;
  outline: none;

  &:focus { border-color: #7367f0; }
  &:disabled { opacity: .55; cursor: progress; }
}
</style>
